/**
 * Formateador de mensajes.
 *
 * Implementa el subconjunto de ICU MessageFormat que el producto necesita:
 * sustitución `{nombre}`, plurales `{n, plural, one {…} other {…}}` y el `#`
 * de la rama elegida, que es la cantidad que decidió el plural.
 *
 * Se escribe a mano en lugar de traer una librería porque son 40 líneas y la
 * alternativa pesa más que todo el catálogo de mensajes junto. Lo que **no**
 * se hace es concatenar cadenas —`'Tienes ' + n + ' archivos'`—: eso funciona
 * en español e inglés y se rompe en cuanto se añade un idioma donde el número
 * va en otra posición o hay más de dos formas de plural.
 */
// Las ramas admiten variables anidadas —`other {{count} archivos}}`—, así que
// su contenido no puede ser «cualquier cosa menos llaves»: se permite un nivel.
const CUERPO = String.raw`(?:[^{}]|\{\w+\})*`;
const PLURAL = new RegExp(
  String.raw`\{(\w+),\s*plural,\s*((?:\s*(?:=\d+|zero|one|two|few|many|other)\s*\{${CUERPO}\})+)\}`,
  'g',
);
const RAMA = new RegExp(String.raw`(=\d+|zero|one|two|few|many|other)\s*\{(${CUERPO})\}`, 'g');
const VARIABLE = /\{(\w+)\}/g;

export type Values = Record<string, string | number>;

export function formatMessage(message: string, values: Values = {}, locale = 'es'): string {
  const conPlurales = message.replace(PLURAL, (_, clave: string, ramas: string) => {
    const cantidad = Number(values[clave] ?? 0);

    const opciones = new Map<string, string>();
    for (const [, selector, texto] of ramas.matchAll(RAMA)) {
      if (selector && texto !== undefined) opciones.set(selector, texto);
    }

    // Una coincidencia exacta (`=0`) gana sobre la categoría gramatical: «sin
    // archivos» se lee mejor que «0 archivos», y sólo el autor del texto sabe
    // cuándo merece la pena.
    const exacta = opciones.get(`=${cantidad}`);
    if (exacta !== undefined) return numero(exacta, cantidad, locale);

    // `Intl.PluralRules` conoce las reglas de cada idioma; el árabe tiene seis
    // categorías y el japonés una. Codificarlas a mano sería adivinar.
    const categoria = new Intl.PluralRules(locale).select(cantidad);
    const rama = opciones.get(categoria) ?? opciones.get('other') ?? '';
    return numero(rama, cantidad, locale);
  });

  return conPlurales.replace(VARIABLE, (coincidencia, clave: string) => {
    const valor = values[clave];
    // Se deja el marcador intacto si falta el valor: un hueco visible se
    // corrige; una cadena `undefined` en pantalla pasa desapercibida.
    return valor === undefined ? coincidencia : String(valor);
  });
}

/**
 * El `#` de una rama de plural **es** la cantidad, no un adorno.
 *
 * Se sustituía sólo `{count}`, así que catorce mensajes escritos con la forma
 * corta de ICU —«{count, plural, one {# foto} other {# fotos}}»— salían en
 * pantalla tal cual: «# fotos» bajo el título del álbum, «¿Eliminar # fotos?»
 * antes de mandarlas a la papelera, «dentro de # días» en el aviso de borrado.
 *
 * Va formateado con `Intl.NumberFormat` y no pegado con `String(n)`: el
 * separador de millares cambia con el idioma —el español agrupa a partir de
 * cinco cifras y el inglés a partir de cuatro—, y quien tiene doce mil fotos
 * ve el suyo.
 */
function numero(texto: string, cantidad: number, locale: string): string {
  if (!texto.includes('#')) return texto;
  return texto.replaceAll('#', new Intl.NumberFormat(locale).format(cantidad));
}
